import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationMenu } from "./NotificationMenu";
import { AccountMenu } from "./AccountMenu";
import { HeaderSearch } from "./HeaderSearch";
import { SpaceSwitch } from "./SpaceSwitch";
import { BrandMarkGlyph } from "./BrandMark";

/** 회사 웹메일(카페24 호스팅). 새 창으로 열기만 하는 바로가기. */
const WEBMAIL_URL = "https://webmail.jgind.co.kr";

/**
 * Header — 48px 상단바 (B′ 공간 전환 바, 2026-10-19 미리보기).
 *
 *   PC   : [⌂ 내 업무] │ [● 중앙산업][● 경원산업]                 [검색 ⌘K] [🔔] [김중앙 ▾]
 *   fold : 같은 구성, 법인은 짧은 이름(중앙/경원), 검색은 아이콘
 *   MO   : [로고 마크] [⌂][● 중앙][● 경원]  — 짧은 칩을 바에 바로 노출, 탭하면 전환(바텀시트·▾ 없음)
 *
 * 옛 헤더에서 뺀 것: KPI 회전 알약(KpiPill)·견적 작성/빠른 작업(QuickActionsMenu) — 이번엔 제거만.
 * 웹메일은 옛 헤더처럼 알림 옆 아이콘 링크(2026-10-19 복구). 설정·도움말은 계정 메뉴(AccountMenu).
 */
interface HeaderProps {
  onOpenSettings: () => void;
  onOpenMobileNav: () => void;
}

export function Header({ onOpenSettings, onOpenMobileNav }: HeaderProps) {
  const [notifOpen, setNotifOpen] = useState(false);
  const { items, unreadCount, markRead, markAllRead } = useNotifications();

  return (
    <header className="rmc-header">
      <div className="hd-left">
        <button
          className="hd-icon-btn hd-mobile-burger"
          onClick={onOpenMobileNav}
          aria-label="메뉴 열기"
          style={{ display: "none" }}
        >
          <Icon name="menu" size={20} />
        </button>
        {/* MO 전용 — CSS 가 PC·fold 에서 숨김 */}
        <span className="hd-mo-mark" aria-hidden="true"><BrandMarkGlyph size={16} /></span>
        {/* PC·fold·MO 공통 — 폭에 따라 CSS 가 이름 길이만 바꾼다 */}
        <SpaceSwitch />
      </div>
      <div className="hd-right">
        <HeaderSearch />
        <a
          className="hd-icon-btn"
          href={WEBMAIL_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="회사 웹메일 (새 창)"
          title="회사 웹메일 (새 창)"
        >
          <Icon name="mail" size={20} />
        </a>
        <div className="hd-notif-wrap">
          <button
            className={`hd-icon-btn ${unreadCount > 0 ? "has-dot" : ""}`}
            onClick={() => setNotifOpen((o) => !o)}
            aria-label={unreadCount > 0 ? `알림 ${unreadCount}건` : "알림"}
          >
            <Icon name="bell" size={20} />
            {unreadCount > 0 && <span className="hd-dot" />}
          </button>
          <NotificationMenu
            open={notifOpen}
            onClose={() => setNotifOpen(false)}
            items={items}
            unreadCount={unreadCount}
            onMarkRead={markRead}
            onMarkAllRead={markAllRead}
          />
        </div>
        <AccountMenu onOpenSettings={onOpenSettings} />
      </div>
    </header>
  );
}
